import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  ContentCopy,
  Delete,
  Domain,
  Error,
  Language,
  Refresh,
  VerifiedUser,
} from "@mui/icons-material";
import {
  manageApplicationDomain,
  verifyApplicationDomain,
} from "../../services/Application";
import ConfirmDialog from "../../components/ConfirmDialog";

const AppCustomDomainSection = ({ application, appId, onRefresh }) => {
  const [customDomain, setCustomDomain] = useState(
    application?.customDomain || "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [dnsInstructions, setDnsInstructions] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  const handleAddDomain = async () => {
    if (!customDomain.trim()) {
      setError("Please enter a domain");
      return;
    }

    setIsLoading(true);
    setError(null);
    setDnsInstructions(null);
    setMessage(null);

    try {
      const response = await manageApplicationDomain(appId, {
        customDomain: customDomain.trim(),
      });

      if (response.success) {
        setDnsInstructions(response.data.dnsInstructions);
        setMessage("Custom domain configured! Please set up DNS records.");
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to configure domain");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveDomain = async () => {
    setRemoveConfirmOpen(false);
    setIsLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await manageApplicationDomain(appId, {
        removeCustomDomain: true,
      });

      if (response.success) {
        setCustomDomain("");
        setDnsInstructions(null);
        setVerificationResult(null);
        setMessage("Custom domain removed successfully");
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove domain");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyDomain = async () => {
    setIsVerifying(true);
    setVerificationResult(null);
    setError(null);

    try {
      const response = await verifyApplicationDomain(appId);
      setVerificationResult(response.data);
      if (response.data.verified && onRefresh) onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to verify domain");
    } finally {
      setIsVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage("Copied to clipboard!");
    setTimeout(() => setMessage(null), 2000);
  };

  const sslEnabled = process.env.REACT_APP_SSL_ENABLED === "true";
  const protocol = sslEnabled ? "https" : "http";

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        <Language sx={{ mr: 1, verticalAlign: "middle" }} />
        Custom Domain
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connect your own domain to this application. Your app will be accessible
        via both the generated subdomain and your custom domain.
      </Typography>

      {/* Success message */}
      <Collapse in={!!message}>
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message}
        </Alert>
      </Collapse>

      {/* Current Domain Info */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Generated Domain
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body1" fontFamily="monospace">
                {application?.generatedDomain}
              </Typography>
              <Tooltip title="Copy URL">
                <IconButton
                  size="small"
                  onClick={() =>
                    copyToClipboard(
                      `${protocol}://${application?.generatedDomain}`,
                    )
                  }
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Custom Domain
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {application?.customDomain ? (
                <>
                  <Typography variant="body1" fontFamily="monospace">
                    {application.customDomain}
                  </Typography>
                  <Chip
                    size="small"
                    icon={
                      application.domainVerified ? <VerifiedUser /> : <Error />
                    }
                    label={
                      application.domainVerified ? "Verified" : "Pending DNS"
                    }
                    color={application.domainVerified ? "success" : "warning"}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not configured
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Add/Update Domain */}
      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Custom Domain"
            placeholder="example.com"
            value={customDomain}
            onChange={(e) => setCustomDomain(e.target.value)}
            disabled={isLoading}
            helperText="Enter domain without https:// or www"
            error={!!error}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: "flex", gap: 1, mt: { xs: 0, md: 1 } }}>
            <Button
              variant="contained"
              startIcon={
                isLoading ? <CircularProgress size={20} /> : <Domain />
              }
              onClick={handleAddDomain}
              disabled={isLoading || !customDomain.trim()}
            >
              {application?.customDomain ? "Update Domain" : "Add Domain"}
            </Button>
            {application?.customDomain && (
              <>
                <Button
                  variant="outlined"
                  startIcon={
                    isVerifying ? <CircularProgress size={20} /> : <Refresh />
                  }
                  onClick={handleVerifyDomain}
                  disabled={isVerifying}
                >
                  Verify DNS
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setRemoveConfirmOpen(true)}
                  disabled={isLoading}
                >
                  Remove
                </Button>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Error Display */}
      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      </Collapse>

      {/* DNS Instructions */}
      <Collapse in={!!dnsInstructions}>
        <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: "action.hover" }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            DNS Configuration Required
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {dnsInstructions?.message}
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dnsInstructions?.records
                ?.filter((r) => !r.optional)
                .map((record, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Chip label={record.type} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography fontFamily="monospace">
                        {record.host}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography fontFamily="monospace">
                          {record.value}
                        </Typography>
                        <Tooltip title="Copy">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(record.value)}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {record.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          {dnsInstructions?.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Important Notes:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {dnsInstructions.notes.map((note, idx) => (
                  <li key={idx}>
                    <Typography variant="body2" color="text.secondary">
                      {note}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}
        </Paper>
      </Collapse>

      {/* Verification Result */}
      <Collapse in={!!verificationResult}>
        <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            {verificationResult?.verified ? (
              <>
                <CheckCircle color="success" />
                <Typography
                  variant="subtitle1"
                  color="success.main"
                  fontWeight={600}
                >
                  Domain Verified Successfully!
                </Typography>
              </>
            ) : (
              <>
                <Error color="warning" />
                <Typography
                  variant="subtitle1"
                  color="warning.main"
                  fontWeight={600}
                >
                  DNS Not Fully Configured
                </Typography>
              </>
            )}
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Record</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Found Values</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {verificationResult?.records?.map((record, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Chip label={record.type} size="small" />
                  </TableCell>
                  <TableCell>{record.host}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={record.status}
                      color={
                        record.status === "correct"
                          ? "success"
                          : record.status === "not_found"
                            ? "error"
                            : "warning"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {record.values?.join(", ") || record.error || "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {verificationResult?.issues?.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Issues Found:</Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {verificationResult.issues.map((issue, idx) => (
                  <li key={idx}>{issue}</li>
                ))}
              </ul>
            </Alert>
          )}
        </Paper>
      </Collapse>

      <ConfirmDialog
        open={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
        onConfirm={handleRemoveDomain}
        title="Remove Custom Domain"
        message="Are you sure you want to remove the custom domain? Traffic will revert to the generated domain."
        confirmText="Remove"
        confirmColor="error"
      />
    </Box>
  );
};

export default AppCustomDomainSection;
